import * as React from "react";
import Box from "@mui/material/Box";
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Select,
  Slide,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import BlockIcon from "@mui/icons-material/Block";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import { RootState } from "../../store";
import { AppointmentBookingMethod, AppointmentCategory, AppointmentType, Organization, SuperOrganization } from "../../types";
import { Colors } from "../../Colors";
import SettingsLayout from "./SettingsLayout";
import { StyledTextField } from "../../components/text-fields/StyledTextField";
import { PrimaryButton } from "../../subcomponents/CustomButton";
import { fetchInsurances } from "../../slices/InsurancesSlice";
import { setSuperOrganization } from "../../slices/AuthSlice";
import { api } from "../../utils/utils";
import { useSnackbar } from "../../providers/SnackbarProvider";

const toKeywordArray = (k?: string | string[]): string[] => (Array.isArray(k) ? k : k ? [k] : []);

const bookingMethodOptions = [
  { label: "Whitespace", value: AppointmentBookingMethod.whitespace },
  { label: "Availability Blocks", value: AppointmentBookingMethod.availabilityBlocks },
  { label: "Whitespace with Available Blocks", value: AppointmentBookingMethod.whitespaceWithAvailableBlocks },
];

type SectionKey = "bookable" | "available" | "blocked" | "ignored";

const SECTION_TARGET_CATEGORY: Record<SectionKey, AppointmentCategory> = {
  bookable: AppointmentCategory.followUp,
  available: AppointmentCategory.available,
  blocked: AppointmentCategory.block,
  ignored: AppointmentCategory.ignore,
};

const SECTION_LABEL: Record<SectionKey, string> = {
  bookable: "Bookable",
  available: "Available Slots",
  blocked: "Blocked",
  ignored: "Ignored",
};

const SECTION_ACCENT: Record<SectionKey, string> = {
  bookable: Colors.primary,
  available: Colors.tertiary,
  blocked: Colors.error,
  ignored: Colors.info,
};

const sectionForCategory = (category?: AppointmentCategory): SectionKey | null => {
  if (category === AppointmentCategory.followUp || category === AppointmentCategory.evaluation) return "bookable";
  if (category === AppointmentCategory.available) return "available";
  if (category === AppointmentCategory.block) return "blocked";
  if (category === AppointmentCategory.ignore) return "ignored";
  return null;
};

type EditableNumberCellProps =
  | {
      value: number;
      onSave: (v: number) => void;
      allowEmpty?: false;
      placeholder?: string;
    }
  | {
      value: number | undefined;
      onSave: (v: number | null) => void;
      allowEmpty: true;
      placeholder?: string;
    };

const EditableNumberCell: React.FC<EditableNumberCellProps> = (props) => {
  const { value, onSave, allowEmpty } = props;
  const placeholder = props.placeholder ?? "—";
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value === undefined ? "" : value.toString());

  React.useEffect(() => {
    setDraft(value === undefined ? "" : value.toString());
  }, [value]);

  const confirm = () => {
    const trimmed = draft.trim();
    if (trimmed === "") {
      if (allowEmpty) {
        (onSave as (v: number | null) => void)(null);
      }
      setEditing(false);
      return;
    }
    const parsed = parseInt(trimmed, 10);
    if (!isNaN(parsed)) (onSave as (v: number) => void)(parsed);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value === undefined ? "" : value.toString());
    setEditing(false);
  };

  if (editing) {
    return (
      <TableCell align="center" sx={{ py: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
          <TextField
            type="number"
            size="small"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirm();
              if (e.key === "Escape") cancel();
            }}
            inputProps={{ min: 0 }}
            sx={{ width: 72 }}
          />
          <IconButton size="small" onClick={confirm}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={cancel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
    );
  }

  return (
    <TableCell align="center" sx={{ py: 1.25, fontSize: "0.85rem", whiteSpace: "nowrap" }}>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <Box component="span" sx={value === undefined ? { color: Colors.grey2 } : undefined}>
          {value === undefined ? placeholder : value}
        </Box>
        <IconButton size="small" onClick={() => setEditing(true)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    </TableCell>
  );
};

const MoveToMenu: React.FC<{
  currentSection: SectionKey;
  onMove: (target: SectionKey) => void;
}> = ({ currentSection, onMove }) => {
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
  const targets = (Object.keys(SECTION_LABEL) as SectionKey[]).filter((s) => s !== currentSection);

  const accent = SECTION_ACCENT[currentSection];

  return (
    <>
      <Tooltip title="Click to move to another section" arrow>
        <Box
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.25,
            pl: 1,
            pr: 0.5,
            py: 0.5,
            backgroundColor: `${accent}1F`,
            color: accent,
            border: `1px solid ${accent}40`,
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "0.75rem",
            fontWeight: 600,
            lineHeight: 1.2,
            userSelect: "none",
            whiteSpace: "nowrap",
            transition: "all 0.15s ease",
            "&:hover": {
              backgroundColor: `${accent}33`,
              borderColor: `${accent}80`,
            },
          }}
        >
          {SECTION_LABEL[currentSection]}
          <ArrowDropDownIcon sx={{ fontSize: 18 }} />
        </Box>
      </Tooltip>
      <Menu open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} MenuListProps={{ dense: true }}>
        <Typography sx={{ px: 2, pt: 1, pb: 0.5, fontSize: "0.7rem", color: Colors.grey2, textTransform: "uppercase", letterSpacing: "0.06em" }}>Move to…</Typography>
        {targets.map((target) => (
          <MenuItem
            key={target}
            onClick={() => {
              onMove(target);
              setAnchor(null);
            }}
            sx={{ fontSize: "0.85rem", minWidth: 180 }}
          >
            {SECTION_LABEL[target]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  accentColor: string;
  banner?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, description, accentColor, banner, children }) => (
  <Box
    sx={{
      mb: 2.5,
      backgroundColor: Colors.bg2,
      border: `1px solid ${Colors.border}`,
      borderRadius: "12px",
      overflow: "hidden",
    }}
  >
    <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${Colors.border}`, display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box sx={{ color: accentColor, display: "inline-flex", alignItems: "center", flexShrink: 0 }}>{icon}</Box>
      <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: Colors.white, flexShrink: 0 }}>{title}</Typography>
      <Typography sx={{ fontSize: "0.75rem", color: Colors.grey2, lineHeight: 1.4 }}>{description}</Typography>
    </Box>
    {banner}
    {children}
  </Box>
);

const EmptyRow: React.FC<{ colSpan: number; message: string }> = ({ colSpan, message }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ py: 3, textAlign: "center", color: Colors.grey2, fontSize: "0.82rem", borderBottom: "none" }}>
      {message}
    </TableCell>
  </TableRow>
);

const headerCellSx = { fontWeight: 600, color: Colors.grey1, py: 1.25, fontSize: "0.78rem" } as const;
const rowSx = { "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.03)" } } as const;
const tableContainerSx = {
  "& .MuiTableCell-root": { borderColor: Colors.border },
  "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": { borderBottom: "none" },
} as const;
const actionHeaderSx = { ...headerCellSx, width: 160, py: 1.25 } as const;
const actionCellSx = { py: 1.25, width: 160 } as const;

const STICKY_BG = Colors.bg2;
const stickyNameHeaderSx = { ...headerCellSx, position: "sticky" as const, left: 0, backgroundColor: STICKY_BG, zIndex: 3, borderRight: `2px solid rgba(255,255,255,0.08)` } as const;
const stickyNameCellSx = { py: 1.25, fontSize: "0.85rem", position: "sticky" as const, left: 0, backgroundColor: STICKY_BG, zIndex: 1, borderRight: `2px solid rgba(255,255,255,0.08)` } as const;

const sortByName = (a: AppointmentType, b: AppointmentType) => a.name.localeCompare(b.name);

type InsuranceOption = { label: string; value: string };

const sameIdSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  for (const id of b) if (!set.has(id)) return false;
  return true;
};

const InsurancePicker: React.FC<{
  value: string[];
  options: InsuranceOption[];
  placeholder: string;
  onChange: (next: string[]) => void;
}> = ({ value, options, placeholder, onChange }) => {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [draft, setDraft] = React.useState<string[]>(value);
  const [search, setSearch] = React.useState("");
  const [showSelectedOnly, setShowSelectedOnly] = React.useState(false);
  const open = !!anchor;

  React.useEffect(() => {
    if (open) setDraft(value);
  }, [open]);

  const total = options.length;
  const draftCount = draft.length;
  const valueCount = value.length;
  const draftAllSelected = total > 0 && draftCount === total;
  const draftNoneSelected = draftCount === 0;
  const draftSet = React.useMemo(() => new Set(draft), [draft]);

  const filtered = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    return options.filter((opt) => {
      if (showSelectedOnly && !draftSet.has(opt.value)) return false;
      if (needle && !opt.label.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [options, search, showSelectedOnly, draftSet]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const handleClose = () => {
    setAnchor(null);
    setSearch("");
    setShowSelectedOnly(false);
    if (!sameIdSet(draft, value)) onChange(draft);
  };

  const toggle = (id: string) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const summary = (() => {
    if (total === 0) return placeholder;
    if (valueCount === 0) return placeholder;
    if (valueCount === total) return `All ${total} insurances`;
    if (valueCount === 1) {
      const match = options.find((o) => o.value === value[0]);
      return match ? match.label : `${valueCount} selected`;
    }
    return `${valueCount} of ${total} selected`;
  })();

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.5,
          mt: "5px",
          pl: "15px",
          pr: "9px",
          py: "15px",
          minWidth: 220,
          backgroundColor: Colors.textfield,
          border: "1px solid rgba(255, 255, 255, 0.23)",
          borderRadius: "10px",
          cursor: "pointer",
          fontSize: "0.9rem",
          color: valueCount === 0 ? Colors.grey2 : "#FFFFFF",
          lineHeight: 1.4375,
          transition: "border-color 0.15s ease",
          "&:hover": { borderColor: "#FFFFFF" },
        }}
      >
        <Typography component="span" sx={{ fontSize: "0.9rem", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {summary}
        </Typography>
        <ArrowDropDownIcon sx={{ fontSize: 24, color: "#FFFFFF", flexShrink: 0 }} />
      </Box>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            width: 360,
            backgroundColor: Colors.bg3,
            borderRadius: "15px",
          },
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${Colors.border}`, display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontSize: "0.78rem", color: Colors.grey1, fontWeight: 500 }}>
              {draftCount} of {total} selected
            </Typography>
            <Box sx={{ display: "flex", gap: 0.25 }}>
              {!draftAllSelected && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setDraft(options.map((o) => o.value))}
                  sx={{ textTransform: "none", fontSize: "0.72rem", minWidth: 0, padding: "0 6px", lineHeight: 1.4 }}
                >
                  Select all
                </Button>
              )}
              {!draftNoneSelected && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setDraft([])}
                  sx={{ textTransform: "none", fontSize: "0.72rem", minWidth: 0, padding: "0 6px", lineHeight: 1.4, color: Colors.grey1 }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>
          <TextField
            size="small"
            placeholder="Search insurances…"
            value={search}
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#FFFFFF" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: Colors.textfield,
                fontSize: "0.9rem",
                color: "#FFFFFF",
              },
            }}
          />
          <Box sx={{ display: "flex", backgroundColor: Colors.textfield, border: "1px solid rgba(255, 255, 255, 0.23)", borderRadius: "10px", p: "3px", gap: "2px" }}>
            {[
              { label: `All (${total})`, value: false as const },
              { label: `Selected (${draftCount})`, value: true as const },
            ].map((opt) => {
              const active = showSelectedOnly === opt.value;
              return (
                <Box
                  key={String(opt.value)}
                  onClick={() => setShowSelectedOnly(opt.value)}
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    py: 0.65,
                    borderRadius: "8px",
                    fontSize: "0.8rem",
                    fontWeight: active ? 500 : 400,
                    color: active ? Colors.white : Colors.grey1,
                    backgroundColor: active ? Colors.bg4 : "transparent",
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      color: Colors.white,
                      backgroundColor: active ? Colors.bg4 : "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
          {filtered.length === 0 ? (
            <Box sx={{ px: 2, py: 3, textAlign: "center", color: Colors.grey2, fontSize: "0.82rem" }}>{showSelectedOnly && draftNoneSelected ? "Nothing selected yet." : "No matches."}</Box>
          ) : (
            filtered.map((opt) => {
              const checked = draftSet.has(opt.value);
              return (
                <Box
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    cursor: "pointer",
                    transition: "background-color 0.1s",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
                  }}
                >
                  <Checkbox checked={checked} size="small" sx={{ p: 0 }} />
                  <Typography sx={{ fontSize: "0.85rem", color: Colors.white }}>{opt.label}</Typography>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};

type OrgOption = { label: string; value: string };

const OrganizationPicker: React.FC<{
  value: string[];
  options: OrgOption[];
  onChange: (next: string[]) => void;
}> = ({ value, options, onChange }) => {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [draft, setDraft] = React.useState<string[]>(value);
  const open = !!anchor;

  React.useEffect(() => {
    if (open) setDraft(value);
  }, [open]);

  const draftSet = React.useMemo(() => new Set(draft), [draft]);
  const total = options.length;
  const valueCount = value.length;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const handleClose = () => {
    setAnchor(null);
    if (!sameIdSet(draft, value)) onChange(draft);
  };

  const toggle = (id: string) => {
    setDraft((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const summary = (() => {
    if (total === 0 || valueCount === 0) return "All locations";
    if (valueCount === total) return "All locations";
    if (valueCount === 1) {
      const match = options.find((o) => o.value === value[0]);
      return match ? match.label : `${valueCount} selected`;
    }
    return `${valueCount} of ${total} locations`;
  })();

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.5,
          mt: "5px",
          pl: "15px",
          pr: "9px",
          py: "15px",
          minWidth: 180,
          backgroundColor: Colors.textfield,
          border: "1px solid rgba(255, 255, 255, 0.23)",
          borderRadius: "10px",
          cursor: "pointer",
          fontSize: "0.9rem",
          color: valueCount === 0 ? Colors.grey2 : "#FFFFFF",
          lineHeight: 1.4375,
          transition: "border-color 0.15s ease",
          "&:hover": { borderColor: "#FFFFFF" },
        }}
      >
        <Typography component="span" sx={{ fontSize: "0.9rem", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {summary}
        </Typography>
        <ArrowDropDownIcon sx={{ fontSize: 24, color: "#FFFFFF", flexShrink: 0 }} />
      </Box>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 240,
            backgroundColor: Colors.bg3,
            borderRadius: "15px",
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${Colors.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "0.78rem", color: Colors.grey1, fontWeight: 500 }}>
            {draft.length === 0 ? "All locations" : `${draft.length} selected`}
          </Typography>
          {draft.length > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={() => setDraft([])}
              sx={{ textTransform: "none", fontSize: "0.72rem", minWidth: 0, padding: "0 6px", lineHeight: 1.4, color: Colors.grey1 }}
            >
              Clear
            </Button>
          )}
        </Box>
        <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
          {options.length === 0 ? (
            <Box sx={{ px: 2, py: 2, textAlign: "center", color: Colors.grey2, fontSize: "0.82rem" }}>No locations found.</Box>
          ) : (
            options.map((opt) => (
              <Box
                key={opt.value}
                onClick={() => toggle(opt.value)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  cursor: "pointer",
                  transition: "background-color 0.1s",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
                }}
              >
                <Checkbox checked={draftSet.has(opt.value)} size="small" sx={{ p: 0 }} />
                <Typography sx={{ fontSize: "0.85rem", color: Colors.white }}>{opt.label}</Typography>
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
};

type AppointmentTypePatch = Omit<Partial<AppointmentType>, "visibleDuration"> & {
  appointmentTypeId: string;
  visibleDuration?: number | null;
};

const BookableByPicker: React.FC<{
  availableId: string;
  selectedBookableIds: string[];
  bookableTypeOptions: { id: string; name: string }[];
  appointmentTypes: AppointmentType[];
  updateAppointmentType: (patch: AppointmentTypePatch) => void;
}> = ({ availableId, selectedBookableIds, bookableTypeOptions, appointmentTypes, updateAppointmentType }) => {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<string[]>(selectedBookableIds);

  const handleOpen = () => {
    setDraft(selectedBookableIds);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    if (sameIdSet(draft, selectedBookableIds)) return;
    const prevSet = new Set(selectedBookableIds);
    const nextSet = new Set(draft);
    const changedIds = [...draft.filter((id) => !prevSet.has(id)), ...selectedBookableIds.filter((id) => !nextSet.has(id))];
    for (const bookableId of changedIds) {
      const target = appointmentTypes.find((t) => t.appointmentTypeId === bookableId);
      const current = target?.availableAppointmentTypeIds ?? [];
      const updated = nextSet.has(bookableId) ? Array.from(new Set([...current, availableId])) : current.filter((id) => id !== availableId);
      updateAppointmentType({
        appointmentTypeId: bookableId,
        availableAppointmentTypeIds: updated,
      });
    }
  };

  const displayValue = open ? draft : selectedBookableIds;
  const hasNoBookable = displayValue.length === 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 220 }}>
      <Select
        multiple
        size="small"
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        value={displayValue}
        onChange={(e) => setDraft(e.target.value as string[])}
        renderValue={(selected) => {
          const ids = selected as string[];
          if (ids.length === 0) {
            return <Typography sx={{ fontSize: "0.8rem", color: Colors.grey2 }}>Select bookable types…</Typography>;
          }
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {ids.map((id) => {
                const match = bookableTypeOptions.find((t) => t.id === id);
                return <Chip key={id} label={match?.name || id} size="small" sx={{ fontSize: "0.75rem", height: 20 }} />;
              })}
            </Box>
          );
        }}
        sx={{
          minWidth: 220,
          ...(hasNoBookable && {
            "& .MuiOutlinedInput-notchedOutline": { borderColor: `${Colors.error}80` },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: Colors.error },
          }),
        }}
        displayEmpty
      >
        {bookableTypeOptions.map((opt) => {
          const selected = displayValue.includes(opt.id);
          return (
            <MenuItem key={opt.id} value={opt.id} dense>
              <Checkbox checked={selected} size="small" sx={{ py: 0.5, pr: 1 }} />
              <ListItemText primary={opt.name} primaryTypographyProps={{ fontSize: "0.85rem" }} />
            </MenuItem>
          );
        })}
      </Select>
      {hasNoBookable && <Typography sx={{ fontSize: "0.7rem", color: Colors.error, lineHeight: 1.3, fontWeight: 500 }}>Bookable types are required for this available block to work.</Typography>}
    </Box>
  );
};

const AppointmentTypesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();
  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>([]);
  const [pendingPatches, setPendingPatches] = React.useState<Record<string, AppointmentTypePatch>>({});
  const [pendingBookingMethod, setPendingBookingMethod] = React.useState<AppointmentBookingMethod | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);

  const user = useSelector((state: RootState) => state.auth.user);
  const superOrganization = useSelector((state: RootState) => state.auth.superOrganization);
  const insurances = useSelector((state: RootState) => state.insurances.insurances);
  const userOrganizations: Organization[] = user?.organizations ?? [];
  const token = user?.token;

  const serverBookingMethod = superOrganization?.appointmentBookingMethod ?? AppointmentBookingMethod.whitespace;
  const bookingMethodDirty = pendingBookingMethod !== null && pendingBookingMethod !== serverBookingMethod;
  const dirtyCount = Object.keys(pendingPatches).length + (bookingMethodDirty ? 1 : 0);
  const isDirty = dirtyCount > 0;
  const locked = saving || syncing;

  const fetchAppointmentTypes = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await api.get("/appointment-types", token);
      const data = response.data as AppointmentType[];
      setAppointmentTypes([...data].sort(sortByName));
      setPendingPatches({});
    } catch (error) {
      console.error("Failed to load appointment types:", error);
      showMessage("Failed to load appointment types. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [token, showMessage]);

  const updateAppointmentType = (patch: AppointmentTypePatch) => {
    if (!patch.appointmentTypeId || locked) return;
    const id = patch.appointmentTypeId;
    // For local state, treat null (clear) as undefined so AppointmentType stays well-typed.
    const localPatch: Partial<AppointmentType> = Object.fromEntries(
      Object.entries(patch).map(([k, v]) => [k, v === null ? undefined : v]),
    ) as Partial<AppointmentType>;
    setAppointmentTypes((prev) => prev.map((at) => (at.appointmentTypeId === id ? { ...at, ...localPatch } : at)));
    setPendingPatches((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...patch, appointmentTypeId: id },
    }));
  };

  const handleSave = async () => {
    if (!token || !isDirty || saving) return;
    setSaving(true);
    const patches = Object.values(pendingPatches);
    try {
      const requests: Promise<unknown>[] = patches.map((patch) => api.put(`/appointment-types/${patch.appointmentTypeId}`, token, patch));
      if (bookingMethodDirty && pendingBookingMethod !== null && superOrganization) {
        requests.push(
          api.patch(`/users/super-organization/${superOrganization.superOrganizationId}`, token, { appointmentBookingMethod: pendingBookingMethod }).then((response) => {
            dispatch(setSuperOrganization(response.data.superOrganization as SuperOrganization));
          }),
        );
      }
      await Promise.all(requests);
      setPendingBookingMethod(null);
      showMessage("Appointment types updated successfully.", "success");
      await fetchAppointmentTypes();
    } catch (error) {
      console.error("Failed to save appointment types:", error);
      showMessage("Failed to save appointment types. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = async () => {
    if (!isDirty || saving) return;
    setPendingBookingMethod(null);
    await fetchAppointmentTypes();
  };

  React.useEffect(() => {
    if (!token) return;
    fetchAppointmentTypes();
    dispatch(fetchInsurances(token));
  }, [token, dispatch, fetchAppointmentTypes]);

  React.useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleSync = async () => {
    if (!token || syncing) return;
    setSyncing(true);
    try {
      await api.post("/appointment-types/initialize", token, {});
      await fetchAppointmentTypes();
      showMessage("Appointment types synced from WebPT.", "success");
    } catch (error) {
      console.error("Failed to sync appointment types:", error);
      showMessage("Failed to sync from WebPT. Please try again.", "error");
    } finally {
      setSyncing(false);
    }
  };

  const insuranceOptions = React.useMemo(() => insurances.map((ins) => ({ label: ins.name, value: ins.insuranceId })), [insurances]);
  const organizationOptions = React.useMemo(
    () =>
      userOrganizations
        .filter((org) => org.superOrganizationId === superOrganization?.superOrganizationId)
        .map((org) => ({ label: org.name, value: org.organizationId })),
    [userOrganizations, superOrganization?.superOrganizationId],
  );

  const bookingMethod = pendingBookingMethod ?? serverBookingMethod;
  const showRespectCapacity = bookingMethod === AppointmentBookingMethod.whitespaceWithAvailableBlocks || bookingMethod === AppointmentBookingMethod.availabilityBlocks;
  const showExclusive = bookingMethod === AppointmentBookingMethod.whitespaceWithAvailableBlocks;

  const handleBookingMethodChange = (method: AppointmentBookingMethod) => {
    if (method === serverBookingMethod) {
      setPendingBookingMethod(null);
    } else {
      setPendingBookingMethod(method);
    }
  };

  const handleMove = (appointmentTypeId: string, target: SectionKey) => {
    updateAppointmentType({
      appointmentTypeId,
      category: SECTION_TARGET_CATEGORY[target],
    });
  };

  const groups = React.useMemo(() => {
    const g: { uncategorized: AppointmentType[]; bookable: AppointmentType[]; available: AppointmentType[]; blocked: AppointmentType[]; ignored: AppointmentType[] } = {
      uncategorized: [],
      bookable: [],
      available: [],
      blocked: [],
      ignored: [],
    };
    for (const at of appointmentTypes) {
      const section = sectionForCategory(at.category);
      if (!section) g.uncategorized.push(at);
      else g[section].push(at);
    }
    return g;
  }, [appointmentTypes]);

  const bookableTypeOptions = React.useMemo(() => groups.bookable.map((opt) => ({ id: opt.appointmentTypeId, name: opt.name })), [groups.bookable]);

  const renderLabel = (at: AppointmentType) => (
    <StyledTextField value={at.label} onChange={(value) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, label: value })} sx={{ width: "100%" }} />
  );

  const renderKeywords = (at: AppointmentType, placeholder: string, field: "keyword" | "appointmentNotesKeyword" = "keyword") => (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      options={[]}
      value={toKeywordArray(at[field])}
      onChange={(_e, newValue) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, [field]: newValue as string[] })}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip {...tagProps} key={key} label={option} size="small" sx={{ fontSize: "0.75rem", height: 20 }} />;
        })
      }
      renderInput={(params) => <TextField {...params} placeholder={placeholder} size="small" />}
      sx={{
        minWidth: 200,
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          backgroundColor: Colors.textfield,
          fontSize: "0.9rem",
          color: "#FFFFFF",
        },
      }}
    />
  );

  const renderInsurances = (at: AppointmentType, placeholder: string) => (
    <InsurancePicker
      value={at.insuranceIds ?? []}
      options={insuranceOptions}
      placeholder={placeholder}
      onChange={(next) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, insuranceIds: next })}
    />
  );

  const renderOrganizations = (at: AppointmentType) => {
    const visibleIds = new Set(organizationOptions.map((o) => o.value));
    const hiddenIds = (at.organizationIds ?? []).filter((id) => !visibleIds.has(id));
    return (
      <OrganizationPicker
        value={(at.organizationIds ?? []).filter((id) => visibleIds.has(id))}
        options={organizationOptions}
        onChange={(next) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, organizationIds: [...hiddenIds, ...next] })}
      />
    );
  };

  const availableSelections = React.useMemo(() => {
    const map = new Map<string, string[]>();
    for (const av of groups.available) map.set(av.appointmentTypeId, []);
    for (const b of groups.bookable) {
      const ids = b.availableAppointmentTypeIds ?? [];
      for (const avId of ids) {
        const arr = map.get(avId);
        if (arr) arr.push(b.appointmentTypeId);
      }
    }
    return map;
  }, [groups.available, groups.bookable]);

  const renderBookableTypesPicker = (at: AppointmentType) => (
    <BookableByPicker
      availableId={at.appointmentTypeId}
      selectedBookableIds={availableSelections.get(at.appointmentTypeId) ?? []}
      bookableTypeOptions={bookableTypeOptions}
      appointmentTypes={appointmentTypes}
      updateAppointmentType={updateAppointmentType}
    />
  );

  const renderActionCell = (at: AppointmentType, currentSection: SectionKey, cellSx = actionCellSx) => (
    <TableCell sx={cellSx}>
      <MoveToMenu currentSection={currentSection} onMove={(target) => handleMove(at.appointmentTypeId, target)} />
    </TableCell>
  );

  return (
    <SettingsLayout>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h5">Appointment Types</Typography>
          <Chip
            label="Company-Wide"
            size="small"
            sx={{
              backgroundColor: `${Colors.info}20`,
              color: Colors.info,
              border: `1px solid ${Colors.info}40`,
              fontSize: "0.7rem",
              height: "20px",
            }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {superOrganization && (
            <>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Typography sx={{ fontSize: "0.7rem", color: Colors.grey2, letterSpacing: "0.04em", textTransform: "uppercase" }}>Booking Method</Typography>
                <Box
                  sx={{
                    display: "flex",
                    backgroundColor: Colors.textfield,
                    border: "1px solid rgba(255, 255, 255, 0.23)",
                    borderRadius: "10px",
                    p: "3px",
                    gap: "2px",
                  }}
                >
                  {bookingMethodOptions.map((opt) => {
                    const active = bookingMethod === opt.value;
                    return (
                      <Box
                        key={opt.value}
                        onClick={() => handleBookingMethodChange(opt.value)}
                        sx={{
                          px: 1.5,
                          py: 0.65,
                          borderRadius: "8px",
                          fontSize: "0.8rem",
                          fontWeight: active ? 500 : 400,
                          color: active ? Colors.white : Colors.grey1,
                          backgroundColor: active ? Colors.bg4 : "transparent",
                          cursor: "pointer",
                          userSelect: "none",
                          transition: "all 0.15s ease",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            color: Colors.white,
                            backgroundColor: active ? Colors.bg4 : "rgba(255,255,255,0.05)",
                          },
                        }}
                      >
                        {opt.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            </>
          )}
          <Tooltip title={isDirty ? "Save or discard your changes before syncing" : ""} arrow disableHoverListener={!isDirty}>
            <span>
              <PrimaryButton size="small" startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />} onClick={handleSync} disabled={syncing || isDirty}>
                Sync from WebPT
              </PrimaryButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ pb: "120px" }}>
          {groups.uncategorized.length > 0 && (
            <SectionCard icon={<WarningAmberIcon fontSize="small" />} title="Needs categorization" description="Pick a section for each so Pencil knows what to do." accentColor={Colors.warning}>
              <TableContainer sx={tableContainerSx}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>WebPT ID</TableCell>
                      <TableCell sx={headerCellSx}>Name</TableCell>
                      <TableCell sx={{ ...headerCellSx, minWidth: 200 }}>Label</TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Duration
                      </TableCell>
                      <TableCell sx={headerCellSx}>Categorize as…</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.uncategorized.map((at) => (
                      <TableRow key={at.appointmentTypeId} sx={rowSx}>
                        <TableCell sx={{ py: 1.25, fontSize: "0.85rem" }}>{at.ehrAppointmentTypeId}</TableCell>
                        <TableCell sx={{ py: 1.25, fontSize: "0.85rem" }}>{at.name}</TableCell>
                        <TableCell sx={{ py: 1.25, minWidth: 200 }}>{renderLabel(at)}</TableCell>
                        <EditableNumberCell value={at.duration} onSave={(v) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, duration: v })} />
                        <TableCell sx={{ py: 1.25 }}>
                          <Select<SectionKey | "">
                            size="small"
                            value=""
                            displayEmpty
                            onChange={(e) => handleMove(at.appointmentTypeId, e.target.value as SectionKey)}
                            sx={{ minWidth: 200 }}
                            renderValue={(value) => (value ? SECTION_LABEL[value as SectionKey] : <Typography sx={{ fontSize: "0.85rem", color: Colors.grey2 }}>Pick a section…</Typography>)}
                          >
                            {(Object.keys(SECTION_LABEL) as SectionKey[]).map((s) => (
                              <MenuItem key={s} value={s}>
                                {SECTION_LABEL[s]}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </SectionCard>
          )}

          <SectionCard
            icon={<CalendarMonthIcon fontSize="small" />}
            title="Bookable"
            description="Patients can book these. Use Keywords or Insurances to route plan-of-care follow-ups onto the right type."
            accentColor={Colors.primary}
          >
            <TableContainer sx={{ ...tableContainerSx, overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={actionHeaderSx}>Section</TableCell>
                    <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>WebPT ID</TableCell>
                    <TableCell sx={stickyNameHeaderSx}>Name</TableCell>
                    <TableCell sx={{ ...headerCellSx, minWidth: 200 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Label
                        <Tooltip title="The appointment type name displayed to patients. If left blank, the Name will be used instead." arrow>
                          <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={headerCellSx}>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        Duration
                        <Tooltip title="The actual appointment length. Used to compute availability and book the slot in WebPT." arrow>
                          <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={headerCellSx}>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        Patient-facing duration
                        <Tooltip title="Duration patients see on their booking link. Leave blank to use the actual Duration." arrow>
                          <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Kind
                        <Tooltip title="Follow-Up is a return visit. Evaluation is for evaluation appointments (experimental)." arrow>
                          <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={headerCellSx}>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        1:1
                        <Tooltip title="When enabled, this appointment type will be single-booked." arrow>
                          <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Case Study Title Keywords
                        <Tooltip title="When set, the default follow-ups in matching patients' plans of care are converted to use this appointment type." arrow>
                          <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Appt Notes Keywords
                        <Tooltip title="When set, patients whose appointment notes contain at least one of these keywords are routed to this appointment type." arrow>
                          <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Insurances
                        <Tooltip title="When set, the default follow-ups in matching patients' plans of care are converted to use this appointment type." arrow>
                          <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Locations
                        <Tooltip title="When set, this appointment type is only offered to patients at the selected locations. Leave empty to allow all locations." arrow>
                          <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.bookable.length === 0 ? (
                    <EmptyRow colSpan={12} message="No bookable types. Sync from WebPT or move types from another section." />
                  ) : (
                    groups.bookable.map((at) => (
                      <TableRow key={at.appointmentTypeId} sx={rowSx}>
                        {renderActionCell(at, "bookable")}
                        <TableCell sx={{ py: 1.25, fontSize: "0.85rem" }}>{at.ehrAppointmentTypeId}</TableCell>
                        <TableCell sx={stickyNameCellSx}>{at.name}</TableCell>
                        <TableCell sx={{ py: 1.25, minWidth: 200 }}>{renderLabel(at)}</TableCell>
                        <EditableNumberCell value={at.duration} onSave={(v) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, duration: v })} />
                        <EditableNumberCell
                          value={at.visibleDuration}
                          allowEmpty
                          onSave={(v) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, visibleDuration: v })}
                        />
                        <TableCell sx={{ py: 1.25 }}>
                          <Select<AppointmentCategory>
                            size="small"
                            value={at.category === AppointmentCategory.evaluation ? AppointmentCategory.evaluation : AppointmentCategory.followUp}
                            onChange={(e) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, category: e.target.value as AppointmentCategory })}
                            sx={{ minWidth: 140 }}
                          >
                            <MenuItem value={AppointmentCategory.followUp}>Follow-Up</MenuItem>
                            <MenuItem value={AppointmentCategory.evaluation}>Evaluation</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.25 }}>
                          <Switch size="small" checked={at.isOneOnOne === true} onChange={(e) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, isOneOnOne: e.target.checked })} />
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>{renderKeywords(at, "Type and press Enter")}</TableCell>
                        <TableCell sx={{ py: 1.25 }}>{renderKeywords(at, "Type and press Enter", "appointmentNotesKeyword")}</TableCell>
                        <TableCell sx={{ py: 1.25, minWidth: 220 }}>{renderInsurances(at, "Select insurances")}</TableCell>
                        <TableCell sx={{ py: 1.25, minWidth: 180 }}>{renderOrganizations(at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          {groups.available.length > 0 && (
            <SectionCard
              icon={<ScheduleIcon fontSize="small" />}
              title="Available Slots"
              description="Open slots that bookable appointments can fill. Keywords and Insurances act as optional extra filters."
              accentColor={Colors.tertiary}
              banner={
                bookingMethod === AppointmentBookingMethod.whitespace ? (
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: `${Colors.warning}14`,
                      borderBottom: `1px solid ${Colors.border}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 16, color: Colors.warning }} />
                    <Typography sx={{ fontSize: "0.78rem", color: Colors.grey0 }}>
                      Your booking method is Whitespace, so these aren&apos;t being used right now. Switch to Availability Blocks (or Whitespace with Available Blocks) to activate them.
                    </Typography>
                  </Box>
                ) : null
              }
            >
              <TableContainer sx={{ ...tableContainerSx, overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={actionHeaderSx}>Section</TableCell>
                      <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>WebPT ID</TableCell>
                      <TableCell sx={stickyNameHeaderSx}>Name</TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Duration
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          Bookable by *
                          <Tooltip
                            arrow
                            title={
                              <Box sx={{ p: 0.5, maxWidth: 320 }}>
                                <Box sx={{ mb: 1, fontSize: "0.8rem", fontWeight: 600 }}>Required.</Box>
                                <Box sx={{ fontSize: "0.75rem", opacity: 0.85, lineHeight: 1.4 }}>
                                  Bookable types that can use this Available slot. The appointment booked by the patient will replace this slot in WebPT.
                                </Box>
                              </Box>
                            }
                          >
                            <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          Keywords
                          <Tooltip title="The patient's case study must contain at least one of these keywords to book this slot. If Insurances are also set, matching either is enough." arrow>
                            <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          Insurances
                          <Tooltip title="The patient's insurance must match at least one of these to book this slot. If Keywords are also set, matching either is enough." arrow>
                            <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {showRespectCapacity && (
                        <TableCell align="center" sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            Respect capacity
                            <Tooltip
                              arrow
                              title={
                                <Box sx={{ p: 0.5, maxWidth: 320 }}>
                                  <Box sx={{ mb: 1, fontSize: "0.8rem" }}>Controls how this slot interacts with the provider&apos;s simultaneous-patient limit.</Box>
                                  <Box sx={{ mb: 0.75, lineHeight: 1.4 }}>
                                    <Box component="span" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                                      On:{" "}
                                    </Box>
                                    <Box component="span" sx={{ fontSize: "0.75rem", opacity: 0.85 }}>
                                      The slot is only offered when the provider has capacity.
                                    </Box>
                                  </Box>
                                  <Box sx={{ lineHeight: 1.4 }}>
                                    <Box component="span" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                                      Off:{" "}
                                    </Box>
                                    <Box component="span" sx={{ fontSize: "0.75rem", opacity: 0.85 }}>
                                      The slot is always offered, even if the provider is at capacity.
                                    </Box>
                                  </Box>
                                </Box>
                              }
                            >
                              <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                      {showRespectCapacity && (
                        <TableCell align="center" sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            1:1
                            <Tooltip title="When enabled, this available block enforces single-booking — no other appointments can overlap this slot." arrow>
                              <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                      {showExclusive && (
                        <TableCell align="center" sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            Exclusive
                            <Tooltip
                              arrow
                              title={
                                <Box sx={{ p: 0.5, maxWidth: 320 }}>
                                  <Box sx={{ mb: 1, fontSize: "0.8rem" }}>Controls whether bookable types in this slot can also be booked in whitespace gaps.</Box>
                                  <Box sx={{ mb: 0.75, lineHeight: 1.4 }}>
                                    <Box component="span" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                                      On:{" "}
                                    </Box>
                                    <Box component="span" sx={{ fontSize: "0.75rem", opacity: 0.85 }}>
                                      Bookable types can ONLY be booked in available blocks — whitespace is excluded.
                                    </Box>
                                  </Box>
                                  <Box sx={{ lineHeight: 1.4 }}>
                                    <Box component="span" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                                      Off:{" "}
                                    </Box>
                                    <Box component="span" sx={{ fontSize: "0.75rem", opacity: 0.85 }}>
                                      Bookable types can be booked in both available blocks and whitespace gaps.
                                    </Box>
                                  </Box>
                                </Box>
                              }
                            >
                              <HelpOutlineIcon sx={{ fontSize: 16, color: Colors.grey2, cursor: "pointer" }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.available.length === 0 ? (
                      <EmptyRow colSpan={showExclusive ? 10 : showRespectCapacity ? 9 : 7} message="No available slots. Sync from WebPT or move types from another section." />
                    ) : (
                      groups.available.map((at) => (
                        <TableRow key={at.appointmentTypeId} sx={rowSx}>
                          {renderActionCell(at, "available")}
                          <TableCell sx={{ py: 1.25, fontSize: "0.85rem" }}>{at.ehrAppointmentTypeId}</TableCell>
                          <TableCell sx={stickyNameCellSx}>{at.name}</TableCell>
                          <EditableNumberCell value={at.duration} onSave={(v) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, duration: v })} />
                          <TableCell sx={{ py: 1.25 }}>{renderBookableTypesPicker(at)}</TableCell>
                          <TableCell sx={{ py: 1.25 }}>{renderKeywords(at, "Type and press Enter")}</TableCell>
                          <TableCell sx={{ py: 1.25, minWidth: 220 }}>{renderInsurances(at, "Filter by insurance (optional)")}</TableCell>
                          {showRespectCapacity && (
                            <TableCell align="center" sx={{ py: 1.25 }}>
                              <Switch
                                size="small"
                                checked={at.transparency === false}
                                onChange={(e) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, transparency: !e.target.checked })}
                              />
                            </TableCell>
                          )}
                          {showRespectCapacity && (
                            <TableCell align="center" sx={{ py: 1.25 }}>
                              <Switch
                                size="small"
                                checked={at.isOneOnOne === true}
                                onChange={(e) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, isOneOnOne: e.target.checked })}
                              />
                            </TableCell>
                          )}
                          {showExclusive && (
                            <TableCell align="center" sx={{ py: 1.25 }}>
                              <Switch
                                size="small"
                                checked={at.exclusive === true}
                                onChange={(e) => updateAppointmentType({ appointmentTypeId: at.appointmentTypeId, exclusive: e.target.checked })}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </SectionCard>
          )}

          <SectionCard icon={<BlockIcon fontSize="small" />} title="Blocked" description="Pencil never schedules over these. Use for lunch, admin time, meetings." accentColor={Colors.error}>
            <TableContainer sx={tableContainerSx}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={actionHeaderSx}>Section</TableCell>
                    <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>WebPT ID</TableCell>
                    <TableCell sx={headerCellSx}>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.blocked.length === 0 ? (
                    <EmptyRow colSpan={3} message="Nothing blocked. Move types here from another section." />
                  ) : (
                    groups.blocked.map((at) => (
                      <TableRow key={at.appointmentTypeId} sx={rowSx}>
                        {renderActionCell(at, "blocked")}
                        <TableCell sx={{ py: 1.25, fontSize: "0.85rem" }}>{at.ehrAppointmentTypeId}</TableCell>
                        <TableCell sx={{ py: 1.25, fontSize: "0.85rem" }}>{at.name}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          <SectionCard icon={<VisibilityOffIcon fontSize="small" />} title="Ignored" description="Pencil pretends these don't exist on the schedule." accentColor={Colors.info}>
            <TableContainer sx={tableContainerSx}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={actionHeaderSx}>Section</TableCell>
                    <TableCell sx={{ ...headerCellSx, whiteSpace: "nowrap" }}>WebPT ID</TableCell>
                    <TableCell sx={headerCellSx}>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.ignored.length === 0 ? (
                    <EmptyRow colSpan={3} message="Nothing ignored. Move types here from another section." />
                  ) : (
                    groups.ignored.map((at) => (
                      <TableRow key={at.appointmentTypeId} sx={rowSx}>
                        {renderActionCell(at, "ignored")}
                        <TableCell sx={{ py: 1.25, fontSize: "0.85rem" }}>{at.ehrAppointmentTypeId}</TableCell>
                        <TableCell sx={{ py: 1.25, fontSize: "0.85rem" }}>{at.name}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Box>
      )}

      <Slide direction="up" in={isDirty && !loading} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "sticky",
            bottom: 20,
            mx: "auto",
            mt: 3,
            width: "fit-content",
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: 2,
            pr: 0.75,
            py: 0.75,
            backgroundColor: Colors.bg3,
            border: `1px solid ${Colors.border}`,
            borderRadius: "999px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
            zIndex: 1100,
          }}
        >
          <Typography sx={{ fontSize: "0.78rem", color: Colors.grey0, whiteSpace: "nowrap" }}>
            {dirtyCount} unsaved {dirtyCount === 1 ? "change" : "changes"}
          </Typography>
          <Button
            onClick={handleDiscard}
            disabled={saving}
            size="small"
            sx={{
              textTransform: "none",
              color: Colors.grey1,
              borderRadius: "999px",
              minWidth: 0,
              px: 1.5,
              "&:hover": { color: Colors.white, backgroundColor: "rgba(255,255,255,0.05)" },
            }}
          >
            Discard
          </Button>
          <PrimaryButton onClick={handleSave} disabled={saving} size="small" sx={{ borderRadius: "999px" }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
          </PrimaryButton>
        </Box>
      </Slide>
    </SettingsLayout>
  );
};

export default AppointmentTypesPage;
